/**
 * Migration Runner
 *
 * Console commands for running org-scoped storage migration
 */

import { getOrgId } from '../getOrgId';
import {
  scanForMigration,
  createBackup,
  performMigration,
  rollbackMigration,
  cleanupBackups
} from '../../storage/migrations/migrateToOrgScoped';
import { FLAGS } from '../../config/featureFlags';

/**
 * Main migration workflow
 * Call this from browser console: window.runOrgScopedMigration()
 */
export async function runOrgScopedMigration() {
  console.log('\n🚀 Starting Organization-Scoped Storage Migration\n');

  // Step 1: Check if feature flag is enabled
  if (!FLAGS.ORG_SCOPED_STORAGE) {
    console.error('❌ ERROR: Feature flag is OFF');
    console.log('📝 Enable first with: window.enableOrgScopedStorage()');
    console.log('📝 Then reload the page');
    return { success: false, error: 'Feature flag not enabled' };
  }

  console.log('✅ Feature flag: ENABLED');

  // Step 2: Get current organization ID
  const orgId = getOrgId();
  if (!orgId || orgId === 'default-org') {
    console.warn('⚠️ No valid organization ID found (expected if not logged in or feature flag disabled)');
    console.log('📝 Make sure you are logged in');
    return { success: false, error: 'No organization ID' };
  }

  console.log(`✅ Organization ID: ${orgId}`);

  // Step 3: Scan for keys to migrate
  console.log('\n📊 Scanning localStorage...');
  const scanResult = scanForMigration();

  console.log(`   Found ${scanResult.keysToMigrate.length} keys to migrate`);
  console.log(`   Skipped ${scanResult.skippedKeys.length} keys (excluded or invalid)`);
  console.log(`   Already migrated: ${scanResult.alreadyMigrated.length} keys`);
  console.log(`   Total data size: ${(scanResult.totalSize / 1024).toFixed(2)} KB`);

  if (scanResult.keysToMigrate.length === 0) {
    console.log('\n✅ Nothing to migrate! All keys already in new format.');
    return { success: true, migrated: 0 };
  }

  // Step 4: Show preview
  console.log('\n📋 Keys to migrate:');
  for (const item of scanResult.keysToMigrate.slice(0, 5)) {
    const newKey = `${item.module}_${orgId}_${item.vesselId}`;
    console.log(`   ${item.oldKey} → ${newKey}`);
  }
  if (scanResult.keysToMigrate.length > 5) {
    console.log(`   ... and ${scanResult.keysToMigrate.length - 5} more`);
  }

  // Step 5: Create backup
  console.log('\n💾 Creating backup...');
  const backupResult = createBackup(scanResult.keysToMigrate);

  if (!backupResult.success) {
    console.error('❌ Backup failed:', backupResult.error);
    return { success: false, error: 'Backup failed' };
  }

  console.log(`✅ Backed up ${backupResult.backupKeys.length} keys`);

  // Step 6: Perform migration
  console.log('\n🔄 Migrating keys...');
  const migrationResult = performMigration(scanResult.keysToMigrate, orgId);

  if (!migrationResult.success) {
    console.error('❌ Migration failed:', migrationResult.error);
    console.log('📝 Run window.rollbackOrgScopedMigration() to restore backup');
    return { success: false, error: 'Migration failed' };
  }

  console.log(`✅ Migrated ${migrationResult.migrated.length} keys`);

  if (migrationResult.failed.length > 0) {
    console.warn(`⚠️  Failed to migrate ${migrationResult.failed.length} keys:`);
    for (const item of migrationResult.failed) {
      console.warn(`   ${item.oldKey}: ${item.reason}`);
    }
  }

  // Step 7: Success
  console.log('\n✅ MIGRATION COMPLETE!');
  console.log('📝 Next steps:');
  console.log('   1. Reload the page to see new storage keys in action');
  console.log('   2. Test the app thoroughly');
  console.log('   3. If issues: window.rollbackOrgScopedMigration()');
  console.log('   4. After 48h stable: window.cleanupOrgScopedBackups()');

  return {
    success: true,
    migrated: migrationResult.migrated.length,
    failed: migrationResult.failed.length
  };
}

/**
 * Rollback migration
 * Call from console: window.rollbackOrgScopedMigration()
 */
export function rollbackOrgScopedMigrationWrapper() {
  console.log('\n🔙 Rolling back migration...');

  const result = rollbackMigration();

  if (!result.success) {
    console.error('❌ Rollback failed:', result.error);
    return result;
  }

  console.log(`✅ Restored ${result.restored.length} keys`);
  console.log('📝 Reload the page to use restored keys');
  console.log('📝 Don\'t forget to disable feature flag: window.disableOrgScopedStorage()');

  return result;
}

/**
 * Clean up backups after stable period
 * Call from console: window.cleanupOrgScopedBackups()
 */
export function cleanupOrgScopedBackupsWrapper() {
  console.log('\n🧹 Cleaning up backups...');

  const result = cleanupBackups();

  if (!result.success) {
    console.error('❌ Cleanup failed:', result.error);
    return result;
  }

  console.log(`✅ Cleaned up ${result.cleaned} backup keys`);
  console.log('📝 Backups permanently deleted');

  return result;
}

// Expose to browser console
if (typeof window !== 'undefined') {
  window.runOrgScopedMigration = runOrgScopedMigration;
  window.rollbackOrgScopedMigration = rollbackOrgScopedMigrationWrapper;
  window.cleanupOrgScopedBackups = cleanupOrgScopedBackupsWrapper;
}
